import { useQuery } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface TenantProfile {
  id: string
  profile_id: string
  canonical_id: string
  coverage_level: string
  stability_indicator: string
  total_observation_count: number
  last_distillation: string | null
  is_active: boolean
  created_at: string
  updated_at: string | null
}

export interface TenantProfileDetail {
  id: string
  profile_id: string
  canonical_id: string
  tenant_subject_id: string | null
  coverage_level: string
  stability_indicator: string
  total_observation_count: number
  last_distillation: string | null
  is_active: boolean
  signals_universal: Record<string, any>
  signals_behavioral: Record<string, any>
  signals_regulated: Record<string, any>
  signals_pragmatic: Record<string, any>
  domain_preferences: Record<string, any> | null
  persona_references: Record<string, any> | null
  created_at: string
  updated_at: string | null
}

interface TenantProfileListResponse {
  profiles: TenantProfile[]
  total: number
  limit: number
  offset: number
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  profiles: (search?: string) => ['tenant-profiles', search],
  profile: (profileId: string) => ['tenant-profile', profileId],
}

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Fetch profiles scoped to the current tenant.
 * For consumers, returns only their own profile.
 */
export function useTenantProfiles(search?: string) {
  return useQuery<TenantProfileListResponse>({
    queryKey: QUERY_KEYS.profiles(search),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      params.append('limit', '50')

      const res = await api.get(`/tenants/profiles?${params.toString()}`)
      return res.data
    },
  })
}

/**
 * Fetch a single profile detail within tenant scope.
 */
export function useTenantProfile(profileId: string | null) {
  return useQuery<TenantProfileDetail>({
    queryKey: QUERY_KEYS.profile(profileId || ''),
    queryFn: async () => {
      const res = await api.get(`/tenants/profiles/${profileId}`)
      return res.data
    },
    enabled: !!profileId,
  })
}
